import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  BookHeart,
  CalendarDays,
  Check,
  Cloud,
  Loader2,
  LockKeyhole,
  MoonStar,
  Sparkles,
  Stars,
  WandSparkles,
} from "lucide-react";
import { z } from "zod";
import { AiConsentDialog, hasAiConsent } from "@/components/AiConsentDialog";
import { Button } from "@/components/ui/button";
import { DREAM_EMOTIONS, DREAM_SYMBOLS, getDreamOption } from "@/lib/dream-options";
import { readSavedDream } from "@/lib/dream-reading.functions";
import { useSleepStore } from "@/lib/use-sleep-store";
import { withMemberGate } from "@/lib/with-member-gate";

type DreamEntry = {
  id: string;
  date: string;
  title: string;
  description: string;
  emotions: string[];
  people: string;
  locations: string;
  symbols: string[];
  themes: string[];
  reflection: string;
};

const searchSchema = z.object({ dream: z.string().optional().catch(undefined) });

export const Route = createFileRoute("/dream-reading")({
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Gentle Dream Reading — Recovery Trauma™" },
      { name: "description", content: "A private, gentle reflection on one of your saved dreams." },
      { property: "og:title", content: "Gentle Dream Reading — Recovery Trauma™" },
      { property: "og:description", content: "A private, gentle reflection on one of your saved dreams." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: withMemberGate(DreamReadingPage, "Dream Reading"),
});

function DreamReadingPage() {
  const { dream: requestedDreamId } = Route.useSearch();
  const { items: dreams, ready } = useSleepStore<DreamEntry>("dream");
  const createReading = useServerFn(readSavedDream);
  const [selectedId, setSelectedId] = useState(requestedDreamId ?? "");
  const [reading, setReading] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [consentOpen, setConsentOpen] = useState(false);

  useEffect(() => {
    if (!selectedId && dreams[0]) setSelectedId(dreams[0].id);
  }, [dreams, selectedId]);

  const selected = useMemo(
    () => dreams.find((dream) => dream.id === selectedId) ?? dreams[0],
    [dreams, selectedId],
  );

  async function requestReading() {
    if (!selected || loading) return;
    setLoading(true);
    setError("");
    setReading("");
    try {
      const result = await createReading({
        data: { dreamId: selected.id },
      });
      setReading(result.reading);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We couldn't create this reading. Your saved dream is unchanged.");
    } finally {
      setLoading(false);
    }
  }

  function beginReading() {
    if (hasAiConsent()) void requestReading();
    else setConsentOpen(true);
  }

  if (!ready && dreams.length === 0) {
    return <div className="grid min-h-[55vh] place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[var(--gold-deep)]" aria-label="Loading saved dreams" /></div>;
  }

  return (
    <main className="min-h-dvh bg-background pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-16">
      <div className="mx-auto w-full max-w-5xl px-4 pb-8 pt-4 sm:px-6 sm:pt-8">
        <header className="relative overflow-hidden border-b border-gold/30 px-2 pb-7 pt-4 text-center sm:pb-9">
          <Cloud className="absolute start-2 top-7 h-9 w-9 text-muted-foreground/25" aria-hidden />
          <Stars className="absolute end-3 top-3 h-8 w-8 text-[var(--gold-deep)]/55" aria-hidden />
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-gold/40 bg-[var(--qa-gold-tint)] text-[var(--gold-deep)] shadow-sm">
            <MoonStar className="h-8 w-8" aria-hidden />
          </span>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">Your private dream space</p>
          <h1 className="mt-1 font-display text-3xl text-foreground sm:text-4xl">Gentle Dream Reading</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Choose one saved dream and receive possibilities to explore — never a diagnosis, prediction or fixed meaning.
          </p>
        </header>

        {dreams.length === 0 ? (
          <section className="mx-auto mt-8 max-w-xl border-y border-gold/25 py-10 text-center">
            <BookHeart className="mx-auto h-9 w-9 text-[var(--gold-deep)]" aria-hidden />
            <h2 className="mt-3 font-display text-2xl text-foreground">Your dream archive is empty</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Save a dream first, then return here whenever you would like a gentle reading.</p>
            <Button asChild variant="gold" className="mt-5 rounded-full">
              <Link to="/sleep-dreams">Open Dream Journal</Link>
            </Button>
          </section>
        ) : (
          <div className="mt-7 grid gap-7 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start">
            <section aria-labelledby="choose-dream-heading">
              <h2 id="choose-dream-heading" className="font-display text-xl text-foreground">Choose a saved dream</h2>
              <div className="mt-3 space-y-2">
                {dreams.map((dream) => {
                  const active = selected?.id === dream.id;
                  return (
                    <Button
                      key={dream.id}
                      type="button"
                      variant="outline"
                      aria-pressed={active}
                      onClick={() => { setSelectedId(dream.id); setReading(""); setError(""); }}
                      className={`h-auto w-full justify-start whitespace-normal rounded-2xl px-4 py-3 text-start ${active ? "border-gold bg-gold/15 ring-2 ring-gold ring-offset-2 ring-offset-background" : "border-border/70 bg-card"}`}
                    >
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${active ? "bg-gold/25 text-[var(--gold-deep)]" : "bg-muted text-muted-foreground"}`}>
                        {active ? <Check className="h-5 w-5" /> : <MoonStar className="h-5 w-5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-display text-base text-foreground">{dream.title || "Untitled dream"}</span>
                        <span className="mt-0.5 flex items-center gap-1 text-xs font-normal text-muted-foreground"><CalendarDays className="h-3 w-3" /> {dream.date}</span>
                      </span>
                    </Button>
                  );
                })}
              </div>
            </section>

            {selected ? (
              <section className="overflow-hidden rounded-3xl border border-gold/35 bg-card shadow-[var(--shadow-soft-lg)]">
                <div className="border-b border-gold/25 bg-[var(--qa-gold-tint)]/55 px-5 py-5 sm:px-7">
                  <p className="text-xs text-muted-foreground">{selected.date}</p>
                  <h2 className="mt-1 font-display text-2xl text-foreground">{selected.title || "Untitled dream"}</h2>
                  <p className="mt-3 line-clamp-6 whitespace-pre-wrap text-sm leading-7 text-foreground/80">{selected.description}</p>
                  <VisualTags labels={selected.emotions} options={DREAM_EMOTIONS} />
                  <VisualTags labels={selected.symbols} options={DREAM_SYMBOLS} />
                </div>

                <div className="p-5 sm:p-7">
                  {!reading ? (
                    <div className="text-center">
                      <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-gold/15 text-[var(--gold-deep)]"><WandSparkles className="h-6 w-6" /></span>
                      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                        Only this selected dream is shared when you ask for a reading. The reading is not saved.
                      </p>
                      <Button type="button" variant="gold" onClick={beginReading} disabled={loading || !selected.description.trim()} className="mt-5 w-full rounded-full sm:w-auto sm:px-8">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading gently…</> : <><Sparkles className="h-4 w-4" /> Read this dream</>}
                      </Button>
                      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><LockKeyhole className="h-3.5 w-3.5" /> Private and requested by you</p>
                    </div>
                  ) : (
                    <div aria-live="polite">
                      <div className="mb-5 flex items-center gap-2 text-[var(--gold-deep)]"><Sparkles className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-wider">Your gentle reflection</span></div>
                      <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground/85 prose-li:text-foreground/85 prose-strong:text-foreground">
                        <ReactMarkdown>{reading}</ReactMarkdown>
                      </div>
                      <div className="mt-6 border-t border-gold/25 pt-4 text-xs leading-relaxed text-muted-foreground">
                        There is no single correct meaning for a dream. Keep only what feels useful; your own understanding matters most.
                      </div>
                      <Button type="button" variant="goldOutline" onClick={beginReading} disabled={loading} className="mt-4 rounded-full">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Reflect again
                      </Button>
                    </div>
                  )}
                  {error ? <p role="alert" className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p> : null}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>

      <AiConsentDialog
        open={consentOpen}
        onDecline={() => setConsentOpen(false)}
        onAgree={() => { setConsentOpen(false); void requestReading(); }}
      />
    </main>
  );
}

function VisualTags({ labels, options }: { labels: string[]; options: typeof DREAM_EMOTIONS }) {
  if (labels.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {labels.map((label) => {
        const option = getDreamOption(label, options);
        const Icon = option?.icon ?? Sparkles;
        return <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-card/75 px-2.5 py-1 text-xs text-foreground"><Icon className="h-3.5 w-3.5 text-[var(--gold-deep)]" />{label}</span>;
      })}
    </div>
  );
}